import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { useTransacoes } from '../../servicos/transacoes/useTransacoes';
import { useTransacaoContexto } from '../../contextos/TransacaoContexto';
// Hooks de serviço (não os contextos crus): eles disparam a busca inicial
// quando ainda não carregou (perfil.carregar*). Como esta sheet fica montada
// globalmente no AppShell, categorias/contas/cartões carregam ao entrar em
// qualquer tela do app — sem depender de visitar a tela de cadastro antes.
import { useCategorias } from '../../servicos/categorias/useCategorias';
import { useContas } from '../../servicos/contas/useContas';
import { useCartaoCreditos } from '../../servicos/cartaoCredito/useCartaoCredito';
import { usePerfilContexto } from '../../contextos/PerfilContexto';
import { useAtualizacaoContexto } from '../../contextos/AtualizacaoContexto';
// Imports diretos dos arquivos (NÃO do barrel src/ui) para evitar ciclo com AppShell.
import Button from '../../ui/Button';
import Calendar from '../../ui/Calendar';
import Chip from '../../ui/Chip';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import MoneyInput from '../../ui/MoneyInput';
import SegmentedControl from '../../ui/SegmentedControl';
import SelectMenu from '../../ui/SelectMenu';
import Sheet from '../../ui/Sheet';
import {
  EstadoTransacaoForm,
  ModoEdicao,
  Recorrencia,
  deISO,
  montarPayloadTransacao,
  paraISO,
  rotuloData,
  validarTransacao
} from './transacaoForm';
import styles from './NovaTransacaoSheet.module.css';

export type NovaTransacaoSheetProps = {
  aberto: boolean;
  onFechar: () => void;
  transacao?: Transacao | null;
  tipoInicial?: 'D' | 'R';
};

// IDs EXATOS que o backend armazena (mesma lista do formulário antigo).
const TIPOS_PAGAMENTO = [
  { id: 'PIX', icone: 'qr_code_2' },
  { id: 'Dinheiro', icone: 'payments' },
  { id: 'Cartão de Crédito', icone: 'credit_card' },
  { id: 'Cartão de Debito', icone: 'credit_score' },
  { id: 'Boleto', icone: 'receipt_long' },
  { id: 'Transferência', icone: 'sync_alt' },
  { id: 'VR', icone: 'restaurant' },
  { id: 'VA', icone: 'shopping_cart' }
];

const normalizar = (texto: string) =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Sheet de criação/edição de transação — fluxo repensado: valor em destaque,
 * chips de data/categoria/pagamento, defaults inteligentes e lançamento em série.
 * Contrato de props fixo (importado pelo AppShell).
 */
export default function NovaTransacaoSheet({ aberto, onFechar, transacao, tipoInicial }: NovaTransacaoSheetProps) {
  const navigate = useNavigate();
  const { transacoes } = useTransacaoContexto();
  const { categorias, loading: carregandoCategorias } = useCategorias();
  const { contas } = useContas();
  const { cartoes } = useCartaoCreditos();
  const { perfil } = usePerfilContexto();
  const categoriasPendentes = carregandoCategorias || !!perfil.carregarCategorias;
  const { setAtualizar } = useAtualizacaoContexto();
  const { loading, createTransacao, updateTransacao } = useTransacoes();

  const edicao = !!transacao;

  const [tipo, setTipo] = useState<'D' | 'R'>('D');
  const [valor, setValor] = useState(0);
  const [descricao, setDescricao] = useState('');
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const [data, setData] = useState(paraISO(new Date()));
  const [categoria, setCategoria] = useState('');
  const [pagamento, setPagamento] = useState('');
  const [conta, setConta] = useState('');
  const [cartaoCredito, setCartaoCredito] = useState('');
  const [recorrencia, setRecorrencia] = useState<Recorrencia>('unica');
  const [mesesFixa, setMesesFixa] = useState('12');
  const [parcelas, setParcelas] = useState('2');
  const [modoEdicao, setModoEdicao] = useState<ModoEdicao>('somenteEsta');
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const [calendarioAberto, setCalendarioAberto] = useState(false);

  const descricaoRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef<HTMLDivElement>(null);

  // Fecha o calendário em clique fora.
  useEffect(() => {
    if (!calendarioAberto) return;
    const aoClicarFora = (evento: MouseEvent) => {
      if (dataRef.current && !dataRef.current.contains(evento.target as Node)) setCalendarioAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [calendarioAberto]);

  // (Re)inicializa o formulário sempre que a sheet abre.
  useEffect(() => {
    if (!aberto) return;

    setTipo(transacao?.tipo ?? tipoInicial ?? 'D');
    setValor(transacao?.valor ?? 0);
    setDescricao(transacao?.descricao ?? '');
    setMostrarSugestoes(false);
    setData(transacao ? paraISO(new Date(transacao.data)) : paraISO(new Date()));
    setCategoria(transacao?.categoriaId?.toString() ?? '');
    setRecorrencia('unica');
    setMesesFixa('12');
    setParcelas('2');
    setModoEdicao('somenteEsta');
    setTentouSalvar(false);

    if (transacao) {
      setPagamento(transacao.tipoPagamento ?? '');
      setConta(transacao.contaId?.toString() ?? '');
      setCartaoCredito(transacao.cartaoCreditoId?.toString() ?? '');
    } else {
      // Default inteligente: última forma de pagamento usada.
      const lembrado = localStorage.getItem('ultimoPagamento') ?? '';
      setPagamento(lembrado);
      if (lembrado === 'Cartão de Crédito') {
        setConta('');
        setCartaoCredito(cartoes.length === 1 ? cartoes[0].id?.toString() ?? '' : '');
      } else if (lembrado === 'Dinheiro' || lembrado === '') {
        setConta('');
        setCartaoCredito('');
      } else {
        setCartaoCredito('');
        setConta(contas.length === 1 ? contas[0].id?.toString() ?? '' : '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, transacao, tipoInicial]);

  // Fecha a lista de sugestões em clique fora.
  useEffect(() => {
    if (!mostrarSugestoes) return;
    const aoClicarFora = (evento: MouseEvent) => {
      if (descricaoRef.current && !descricaoRef.current.contains(evento.target as Node)) {
        setMostrarSugestoes(false);
      }
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [mostrarSugestoes]);

  // Últimas ~50 descrições distintas já usadas (sugestões de preenchimento).
  const sugestoesBase = useMemo(() => {
    const vistas = new Set<string>();
    const lista: string[] = [];
    for (let i = transacoes.length - 1; i >= 0; i--) {
      const texto = transacoes[i].descricao?.trim();
      if (!texto) continue;
      const chave = normalizar(texto);
      if (vistas.has(chave)) continue;
      vistas.add(chave);
      lista.push(texto);
      if (lista.length >= 50) break;
    }
    return lista;
  }, [transacoes]);

  const sugestoes =
    mostrarSugestoes && descricao.trim().length >= 2
      ? sugestoesBase
          .filter(
            (texto) =>
              normalizar(texto).includes(normalizar(descricao.trim())) &&
              normalizar(texto) !== normalizar(descricao.trim())
          )
          .slice(0, 6)
      : [];

  // Mesma regra de negócio do formulário antigo ao trocar a forma de pagamento.
  const aoTrocarPagamento = (id: string) => {
    setPagamento(id);
    if (id === 'Cartão de Crédito') {
      setConta('');
      if (cartoes.length === 1) setCartaoCredito(cartoes[0].id?.toString() ?? '');
    } else if (id === 'Dinheiro') {
      setConta('');
      setCartaoCredito('');
    } else {
      setCartaoCredito('');
      if (contas.length === 1) setConta(contas[0].id?.toString() ?? '');
    }
    // Parcelamento só existe no cartão de crédito.
    if (id !== 'Cartão de Crédito' && recorrencia === 'parcelada') setRecorrencia('unica');
  };

  const aoTrocarTipo = (id: string) => {
    if (edicao) return;
    setTipo(id as 'D' | 'R');
    setCategoria('');
  };

  const irParaCategorias = () => {
    onFechar();
    navigate('/categorias');
  };

  // Estado consolidado para a lógica pura (validação e payload) extraída
  // em transacaoForm.ts — comportamento idêntico ao formulário antigo.
  const estado: EstadoTransacaoForm = {
    tipo,
    valor,
    descricao,
    data,
    categoria,
    pagamento,
    conta,
    cartaoCredito,
    recorrencia,
    mesesFixa,
    parcelas,
    modoEdicao,
    edicao,
    transacao
  };

  const erros = validarTransacao(estado);
  const temErro = Object.values(erros).some(Boolean);
  const erroDe = (campo: keyof typeof erros) => (tentouSalvar ? erros[campo] || undefined : undefined);

  const salvar = async (fecharDepois: boolean) => {
    setTentouSalvar(true);
    if (temErro || loading) return;

    if (pagamento) localStorage.setItem('ultimoPagamento', pagamento);

    const payload = montarPayloadTransacao(estado);

    if (edicao && transacao?.id !== undefined) {
      await updateTransacao(transacao.id, payload);
      setAtualizar((atual) => !atual);
      onFechar();
      return;
    }

    await createTransacao(payload);
    setAtualizar((atual) => !atual);

    if (fecharDepois) {
      onFechar();
    } else {
      // Lançamento em série: limpa valor/descrição, mantém data/categoria/pagamento.
      setValor(0);
      setDescricao('');
      setRecorrencia('unica');
      setMesesFixa('12');
      setParcelas('2');
      setTentouSalvar(false);
    }
  };

  const categoriasDoTipo = categorias.filter((cat) => cat.tipo === tipo);
  const opcoesConta = contas.map((c) => ({
    id: c.id?.toString() ?? '',
    rotulo: c.descricao,
    icone: 'account_balance'
  }));
  const opcoesCartao = cartoes.map((c) => ({
    id: c.id?.toString() ?? '',
    rotulo: c.nome,
    icone: 'credit_card'
  }));

  const mostrarCartao = pagamento === 'Cartão de Crédito';
  const mostrarConta = !['', 'Cartão de Crédito', 'Dinheiro'].includes(pagamento);
  const permiteParcelar = pagamento === 'Cartão de Crédito';

  const opcoesRecorrencia = [
    { id: 'unica', rotulo: 'Única' },
    { id: 'fixa', rotulo: 'Fixa mensal', icone: 'repeat' },
    ...(permiteParcelar ? [{ id: 'parcelada', rotulo: 'Parcelada', icone: 'splitscreen' }] : [])
  ];

  const isoHoje = paraISO(new Date());
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  const isoOntem = paraISO(ontem);

  const titulo = edicao ? 'Editar movimentação' : tipo === 'D' ? 'Nova despesa' : 'Nova receita';
  const subtitulo = edicao
    ? transacao?.ehFixa
      ? 'Recorrência mensal'
      : transacao?.ehParcelada
        ? `Parcela ${transacao.parcela ?? 1} de ${transacao.totalParcelas ?? 1}`
        : 'Ajuste os detalhes e salve.'
    : 'Preencha os dados do lançamento.';

  const rodape = edicao ? (
    <>
      <Button variante="fantasma" onClick={onFechar}>
        Cancelar
      </Button>
      <Button variante="primaria" icone="check" carregando={loading} onClick={() => salvar(true)}>
        Salvar alterações
      </Button>
    </>
  ) : (
    <>
      <Button variante="fantasma" onClick={onFechar}>
        Cancelar
      </Button>
      <Button variante="contorno" icone="playlist_add" carregando={loading} onClick={() => salvar(false)}>
        Salvar e lançar outra
      </Button>
      <Button variante="primaria" icone="check" carregando={loading} onClick={() => salvar(true)}>
        Salvar
      </Button>
    </>
  );

  return (
    <Sheet aberto={aberto} onFechar={onFechar} titulo={titulo} subtitulo={subtitulo} largura={520} rodape={rodape}>
      <div className={styles.formulario}>
        <div className={`${styles.tipoLinha} ${edicao ? styles.desabilitado : ''}`}>
          <SegmentedControl
            opcoes={[
              { id: 'D', rotulo: 'Despesa', icone: 'trending_down' },
              { id: 'R', rotulo: 'Receita', icone: 'trending_up' }
            ]}
            valor={tipo}
            onChange={aoTrocarTipo}
          />
        </div>

        <div className={styles.valorDestaque}>
          <MoneyInput valor={valor} onChange={setValor} autoFocus={!edicao} erro={erroDe('valor')} />
        </div>

        <div className={styles.secao} ref={descricaoRef}>
          <span className={styles.secaoTitulo}>Descrição</span>
          <div className={styles.sugestoesWrap}>
            <Input
              valor={descricao}
              onChange={(texto) => {
                setDescricao(texto);
                setMostrarSugestoes(true);
              }}
              icone="description"
              placeholder="Ex.: Mercado, Aluguel, Salário…"
              erro={erroDe('descricao')}
            />
            {sugestoes.length > 0 && (
              <div className={styles.sugestoes}>
                {sugestoes.map((texto) => (
                  <button
                    key={texto}
                    type="button"
                    className={styles.sugestao}
                    onMouseDown={() => {
                      setDescricao(texto);
                      setMostrarSugestoes(false);
                    }}
                  >
                    <Icon nome="history" tamanho={14} />
                    {texto}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.secao}>
          <span className={styles.secaoTitulo}>Data</span>
          <div className={styles.dataLinha}>
            <Chip rotulo="Hoje" icone="today" ativo={data === isoHoje} onClick={() => setData(isoHoje)} />
            <Chip rotulo="Ontem" ativo={data === isoOntem} onClick={() => setData(isoOntem)} />
            <div className={styles.dataInput} ref={dataRef}>
              <button
                type="button"
                className={`${styles.dataBotao} ${erroDe('data') ? styles.dataBotaoErro : ''}`}
                onClick={() => setCalendarioAberto((atual) => !atual)}
              >
                <Icon nome="event" tamanho={18} />
                <span>{rotuloData(data)}</span>
                <Icon nome="expand_more" tamanho={16} />
              </button>
              {calendarioAberto && (
                <div className={styles.calendarioPop}>
                  <Calendar
                    modo="unica"
                    inicio={data ? deISO(data) : new Date()}
                    onChange={(novaData) => {
                      setData(paraISO(novaData));
                      setCalendarioAberto(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.secao}>
          <span className={styles.secaoTitulo}>Categoria</span>
          <div className={styles.gradeCategorias}>
            {categoriasDoTipo.map((cat) => {
              const id = cat.id?.toString() ?? '';
              const ativa = id === categoria;
              return (
                <button
                  key={id}
                  type="button"
                  className={`${styles.catChip} ${ativa ? styles.catChipAtiva : ''}`}
                  onClick={() => setCategoria(ativa ? '' : id)}
                >
                  <span
                    className={styles.catIcone}
                    style={{ background: `${cat.corIdentificacao}1a`, color: cat.corIdentificacao }}
                  >
                    <Icon nome={cat.icone || 'category'} tamanho={16} />
                  </span>
                  <span className={styles.catNome}>{cat.nome}</span>
                </button>
              );
            })}
            {categoriasDoTipo.length === 0 && (
              <span className={styles.semCategorias}>
                {categoriasPendentes
                  ? 'Carregando suas categorias…'
                  : `Você ainda não tem categorias de ${tipo === 'D' ? 'despesa' : 'receita'}.`}
              </span>
            )}
            <button type="button" className={`${styles.catChip} ${styles.gerenciar}`} onClick={irParaCategorias}>
              <span className={styles.catIcone}>
                <Icon nome="settings" tamanho={16} />
              </span>
              <span className={styles.catNome}>Gerenciar</span>
            </button>
          </div>
        </div>

        <div className={styles.secao}>
          <span className={styles.secaoTitulo}>Forma de pagamento</span>
          <div className={styles.chipsPagamento}>
            {TIPOS_PAGAMENTO.map((tp) => (
              <Chip
                key={tp.id}
                rotulo={tp.id}
                icone={tp.icone}
                ativo={pagamento === tp.id}
                onClick={() => aoTrocarPagamento(tp.id)}
              />
            ))}
          </div>
        </div>

        {mostrarCartao && (
          <div className={styles.secao}>
            <SelectMenu
              label="Cartão de crédito"
              valor={cartaoCredito}
              onChange={setCartaoCredito}
              opcoes={opcoesCartao}
              placeholder="Qual cartão?"
            />
          </div>
        )}
        {mostrarConta && (
          <div className={styles.secao}>
            <SelectMenu
              label="Conta"
              valor={conta}
              onChange={setConta}
              opcoes={opcoesConta}
              placeholder="Qual conta?"
            />
          </div>
        )}

        {!edicao && (
          <div className={styles.secao}>
            <span className={styles.secaoTitulo}>Recorrência</span>
            <SegmentedControl
              opcoes={opcoesRecorrencia}
              valor={recorrencia}
              onChange={(id) => setRecorrencia(id as Recorrencia)}
            />
            {!permiteParcelar && (
              <span className={styles.dicaParcela}>
                Parcelamento fica disponível ao escolher Cartão de Crédito.
              </span>
            )}
            {recorrencia === 'fixa' && (
              <Input
                label="Repetir por quantos meses"
                tipo="number"
                valor={mesesFixa}
                onChange={setMesesFixa}
                icone="repeat"
                dica="De 1 a 36 meses, lançada todo mês nesta data."
                erro={erroDe('meses')}
              />
            )}
            {recorrencia === 'parcelada' && permiteParcelar && (
              <Input
                label="Número de parcelas"
                tipo="number"
                valor={parcelas}
                onChange={setParcelas}
                icone="splitscreen"
                dica="De 1 a 12. A primeira parcela é lançada nesta data."
                erro={erroDe('parcelas')}
              />
            )}
          </div>
        )}

        {edicao && (transacao?.ehFixa || transacao?.ehParcelada) && (
          <div className={styles.modoEdicaoBloco}>
            <span className={styles.modoEdicaoTitulo}>Aplicar alteração em:</span>
            <SegmentedControl
              opcoes={[
                { id: 'somenteEsta', rotulo: 'Somente esta' },
                { id: 'futuras', rotulo: 'Esta e futuras' },
                { id: 'todas', rotulo: 'Todas' }
              ]}
              valor={modoEdicao}
              onChange={(id) => setModoEdicao(id as ModoEdicao)}
            />
            <span className={styles.modoEdicaoDica}>
              Este lançamento é {transacao?.ehFixa ? 'uma recorrência mensal' : 'parcelado'}. Escolha o alcance
              da alteração.
            </span>
          </div>
        )}
      </div>
    </Sheet>
  );
}
